import { Injectable } from '@angular/core';
import { ApiHttpClient, ListOption, ListOptionRequest } from '../modules/tcm-core.module';
import { ClaimCreationResultModel, ClaimDevicesSearchRequest, ClaimDevicesSearchRowViewModel, ClaimsCreationRequest, CreateClaimRow, EdiCreationRequest, EdiCreationResultViewModel, GetCreateClaimRowRequest } from '../models/claims';
import { ClaimUnbundleCodeActionEnum } from '../models/enums';
import { ClaimUnbundleCodeCheckExistRequest } from '../models/claim-unbundle-codes';
import { AdpTextFileGenerationRequest } from '../models/claims/adp-text-file-generation-request';

@Injectable({
  providedIn: 'root'
})
export class ClaimService {


  constructor(private readonly httpClient: ApiHttpClient) {
  }

  generateClaimXMLAsync(id: number, cancellationToken?: Promise<any>): Promise<number> {
    return this.httpClient.postJsonAsync('Claims/generateClaimXML', id, cancellationToken);
  }

  /**
   * Generates a claim text file based on the provided AdpTextFileGenerationRequest object.
   * @param request - The request object containing the necessary data for generating the claim text file.
   * @param cancellationToken - Optional cancellation token for the operation.
   * @returns A promise that resolves to the generated claim text file's identifier.
   */
  generateClaimTextAsync(request: AdpTextFileGenerationRequest, cancellationToken?: Promise<any>): Promise<number> {
    return this.httpClient.postJsonAsync('Claims/generateClaimText', request, cancellationToken);
  }

  printCms1500RedFormAsync(withoutLines: boolean, claimIds: number[], cancellationToken?: Promise<any>) {
    const request = {
      withoutLines: withoutLines,
      claimIds: claimIds
    };

    return this.httpClient.getFileAsync("Cms1500Print/RedForm", request, cancellationToken);
  }

  createClaimAsync(saleOrderIds: number[], autoConfirm: boolean, cancellationToken: Promise<any>, forSecondInsurance: boolean = false): Promise<ClaimCreationResultModel[]> {
    const request = {} as ClaimsCreationRequest;
    request.saleOrderIds = saleOrderIds;
    request.autoConfirm = autoConfirm;
    request.forSecondInsurance = forSecondInsurance;
    return this.httpClient.postJsonAsync<ClaimCreationResultModel[]>('Claims/CreateClaimsFromSalesOrders', request, cancellationToken);
  }

  rejectClaim(claimId: number, cancellationToken: Promise<any>): Promise<number> {
    return this.httpClient.postJsonAsync('Claims/RejectClaim', claimId, cancellationToken);
  }

  sendToClearingHouse(claimIds: number[], isForced: boolean, cancellationToken: Promise<any>): Promise<EdiCreationResultViewModel[]> {
    return this.httpClient.postJsonAsync<EdiCreationResultViewModel[]>('Claims/SendClaimsToClearingHouse', { claimIds: claimIds, isForced: isForced } as EdiCreationRequest, cancellationToken);
  }

  checkClaimUnbundlingRuleExistsAsync(id: number, name: any, cancellationToken: Promise<any>) {
    const data = { id: id, name: name };
    return this.httpClient.getJsonAsync<boolean>('ClaimUnbundlingRules/CheckClaimUnbundlingRuleExists', data, cancellationToken, false);
  }

  checkClaimUnbundleCodeExistsAsync(claimUnbundleCodeId: number, claimUnbundlingRuleId: number, action: ClaimUnbundleCodeActionEnum, originCodeId: number, productCategoryId: number, procedureCodeModifierIds: number[], cancellationToken: Promise<any>): Promise<boolean> {
    const request = {
      id: claimUnbundleCodeId,
      claimUnbundlingRuleId: claimUnbundlingRuleId,
      action: action,
      originCodeId: originCodeId,
      productCategoryId: productCategoryId,
      procedureCodeModifierIds: procedureCodeModifierIds || []
    } as ClaimUnbundleCodeCheckExistRequest;
    return this.httpClient.postJsonAsync<boolean>('ClaimUnbundleCodes/CheckClaimUnbundleCodeExists', request, cancellationToken, false);
  }

  deleteClaimUnbundleCodeAsync(id: number, cancellationToken: Promise<any>): Promise<any> {
    return this.httpClient.postJsonAsync('ClaimUnbundleCodes/Delete', id, cancellationToken);
  }

  moveToEDIQueue(claimId: number, cancellationToken: Promise<any>): Promise<any> {
    return this.httpClient.postJsonAsync('Claims/MoveToEDIQueue', claimId, cancellationToken, false);
  }

  moveToPaperQueue(claimId: number, cancellationToken: Promise<any>): Promise<any> {
    return this.httpClient.postJsonAsync('Claims/MoveToPaperQueue', claimId, cancellationToken, false);
  }

  sendClaims(claimIds: number[], cancellationToken: Promise<any>): Promise<any> {
    return this.httpClient.postJsonAsync('Claims/MakeClaimsSent', claimIds, cancellationToken, false);
  }

  getOptionsForNoteAsync(query: string, cancellationToken: Promise<any> = null): Promise<ListOption<number>[]> {
    const request: ListOptionRequest = { searchString: query };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('Claims/OptionsForNote', request, cancellationToken);
  }

  getCreateClaimRowsAsync(salesOrderIds: number[], cancellationToken: Promise<any> = null): Promise<CreateClaimRow[]> {
    const request: GetCreateClaimRowRequest = { salesOrderIds: salesOrderIds };
    return this.httpClient.getJsonAsync<CreateClaimRow[]>('Claims/GetCreateClaimRows', request, cancellationToken);
  }

  ClaimDevicesLinkedSearchAsync(salesOrderId: number, cancellationToken: Promise<any> = null): Promise<ClaimDevicesSearchRowViewModel[]> {
    const request: ClaimDevicesSearchRequest = { salesOrderId: salesOrderId };
    return this.httpClient.getJsonAsync<ClaimDevicesSearchRowViewModel[]>('Claims/ClaimDevicesLinkedSearch', request, cancellationToken);
  }
}
